import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchCaptaincyPollResults,
  POLL_CLOSED_ERROR_CODE,
  voteInCaptaincyPoll
} from '@api-service/captaincy-poll';
import type { CaptaincyPollResults } from '@api-service/captaincy-poll';
import { getCaptaincyPollStatus } from '@lib/captaincy-poll';
import type { CaptaincyPollStatus } from '@lib/captaincy-poll';
import type { CaptaincyPoll } from '@lib/sanity/captaincyPoll';
import { isUserAuthenticated } from '@lib/utils';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0SessionUser } from '@hooks/useAuth0SessionUser';
import { useLeagueContext } from '@hooks/useLeagueContext';

export const CAPTAINCY_POLL_API_PATH = '/api/captaincy-poll';

const CAPTAINCY_POLL_QUERY_KEY = ['captaincy-poll'];
const RESULTS_QUERY_KEY_PREFIX = 'captaincy-poll-results';
const LEAGUE_CONTEXT_QUERY_KEY = ['league-context'];
const ANONYMOUS_VOTER_KEY = 'anonymous';

// Matches the route's one-minute edge cache, so an editor's weekly publish
// reaches a visitor who stays on the site as quickly as one who reloads.
const POLL_STALE_TIME_MS = 60 * 1000;
// Counts move while people vote, but nobody needs them to the second.
const RESULTS_STALE_TIME_MS = 60 * 1000;

const fetchCaptaincyPoll = async (): Promise<CaptaincyPoll | null> => {
  const response = await fetch(CAPTAINCY_POLL_API_PATH);
  if (!response.ok) {
    throw new Error(`Failed to fetch the captaincy poll: ${response.status}`);
  }
  return response.json();
};

const resultsQueryKey = (gameweek: number, voterKey: string) => [
  RESULTS_QUERY_KEY_PREFIX,
  gameweek,
  voterKey
];

/**
 * The poll the editors published and whether it shows: `hidden` until both
 * the poll and the league context have loaded. Pass `isEnabled: false` where
 * the poll cannot show anyway, so nothing is fetched.
 */
export const useCaptaincyPollDefinition = ({
  isEnabled = true
}: { isEnabled?: boolean } = {}): {
  poll: CaptaincyPoll | null;
  status: CaptaincyPollStatus;
} => {
  const { context } = useLeagueContext();
  const { data: poll } = useQuery({
    queryKey: CAPTAINCY_POLL_QUERY_KEY,
    queryFn: fetchCaptaincyPoll,
    staleTime: POLL_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    enabled: isEnabled
  });

  if (!isEnabled || !poll || !context) return { poll: null, status: 'hidden' };
  return { poll, status: getCaptaincyPollStatus(poll.gameweek, context) };
};

/**
 * The votes in a gameweek's poll and the member's own pick, and the mutation
 * that records a pick. Anonymous visitors read the public counts; members wait
 * for their token so their own vote comes back with the counts, and read the
 * public counts if the token cannot be had.
 */
export const useCaptaincyPollResults = (gameweek: number | null) => {
  const queryClient = useQueryClient();
  const { user, isLoading: isUserLoading } = useAuth0SessionUser();
  const isLoggedIn = isUserAuthenticated(user);
  const { accessToken, accessTokenIsFetching } = useAccessToken();
  const didTokenFetchFail =
    isLoggedIn && !accessToken && !accessTokenIsFetching;
  // Keyed by whether the read can name the member, so a token that arrives
  // later starts the member's own read instead of reusing the public one.
  const voterKey =
    isLoggedIn && accessToken ? user?.sub ?? '' : ANONYMOUS_VOTER_KEY;
  const queryKey = resultsQueryKey(gameweek ?? 0, voterKey);

  const resultsQuery = useQuery({
    queryKey,
    queryFn: () =>
      fetchCaptaincyPollResults(gameweek as number, accessToken ?? null),
    enabled:
      gameweek !== null &&
      !isUserLoading &&
      (!isLoggedIn || Boolean(accessToken) || didTokenFetchFail),
    staleTime: RESULTS_STALE_TIME_MS,
    refetchOnWindowFocus: false
  });

  const voteMutation = useMutation({
    mutationFn: async (playerOptaId: string) => {
      if (!accessToken || gameweek === null) {
        throw new Error('No access token available');
      }
      return voteInCaptaincyPoll(accessToken, gameweek, playerOptaId);
    },
    onSuccess: (results: CaptaincyPollResults) => {
      queryClient.setQueryData(queryKey, results);
      queryClient.invalidateQueries({
        queryKey: resultsQueryKey(gameweek ?? 0, ANONYMOUS_VOTER_KEY)
      });
    },
    onError: (error: Error) => {
      // The deadline passed while the card was open: refetch so it shows the
      // final result instead of buttons that can no longer vote.
      if (error.message !== POLL_CLOSED_ERROR_CODE) return;
      queryClient.invalidateQueries({ queryKey: [RESULTS_QUERY_KEY_PREFIX] });
      queryClient.invalidateQueries({ queryKey: LEAGUE_CONTEXT_QUERY_KEY });
    }
  });

  return {
    results: resultsQuery.data ?? null,
    isLoading: resultsQuery.isLoading,
    isError: resultsQuery.isError,
    isLoggedIn,
    vote: voteMutation.mutateAsync,
    isVoting: voteMutation.isLoading,
    voteError: voteMutation.error as Error | null,
    resetVoteError: voteMutation.reset
  };
};
